import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import { Button } from '@/components/common/Button'
import { SearchSuggestions, saveRecentSearch } from './SearchSuggestions'

interface SearchBarProps {
  initialValue?: string
  onSearch?: (query: string) => void
  placeholder?: string
  showButton?: boolean
  showSuggestions?: boolean
}

export function SearchBar({
  initialValue = '',
  onSearch,
  placeholder = 'Search listings...',
  showButton = false,
  showSuggestions = true,
}: SearchBarProps) {
  const navigate = useNavigate()
  const [query, setQuery] = useState(initialValue)
  const [suggestionsOpen, setSuggestionsOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSuggestionsOpen(false)
    saveRecentSearch(query.trim())
    if (onSearch) {
      onSearch(query)
    } else if (query.trim()) {
      navigate(`/browse?q=${encodeURIComponent(query.trim())}`)
    }
  }

  const handleSuggestionSelect = (value: string) => {
    setQuery(value)
    setSuggestionsOpen(false)
    if (onSearch) {
      onSearch(value)
    } else {
      navigate(`/browse?q=${encodeURIComponent(value)}`)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            if (showSuggestions) setSuggestionsOpen(true)
          }}
          onFocus={() => { if (showSuggestions) setSuggestionsOpen(true) }}
          placeholder={placeholder}
          className="input pl-10"
          autoComplete="off"
        />
        {showSuggestions && (
          <SearchSuggestions
            query={query}
            isOpen={suggestionsOpen}
            onClose={() => setSuggestionsOpen(false)}
            onSelect={handleSuggestionSelect}
          />
        )}
      </div>
      {showButton && (
        <Button type="submit">
          Search
        </Button>
      )}
    </form>
  )
}
